import * as Sentry from '@sentry/react';
import { Context } from '@sentry/types';
import { AuthUser } from 'aws-amplify/auth';
import {
  API_ENDPOINT,
  IS_DEV,
  SENTRY_ENABLED,
  SENTRY_ENV,
} from 'src/constants';
import { getUserType } from 'src/utils/getUserType';
import { dayjs } from './dayjs';

const API_URL_REGEX = new RegExp(`^${API_ENDPOINT}`);

// GDPR: error capture and tracing run without consent under legitimate
// interest (Art. 6(1)(f) — service security and stability) and store nothing
// on the device. Session Replay records user behavior and writes to
// sessionStorage, so it only starts after consent: on cookie-banner accept
// (public site) or on sign-in (app, covered by the customer agreement).
Sentry.init({
  enabled: SENTRY_ENABLED,
  dsn: 'https://278093965b0391a1d02823151ef87bbd@o4511485267869696.ingest.us.sentry.io/4511485278027776',
  integrations: [Sentry.browserTracingIntegration()],
  environment: SENTRY_ENV,
  // Performance Monitoring
  tracesSampleRate: SENTRY_ENV === 'main' ? 1.0 : 0.2, //  Capture 100% of the transactions
  tracePropagationTargets: ['localhost', API_URL_REGEX],
  // Session Replay (integration added lazily via enableSentryReplay)
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

let replayEnabled = false;

export const enableSentryReplay = () => {
  if (replayEnabled || !SENTRY_ENABLED) {
    return;
  }
  replayEnabled = true;
  Sentry.addIntegration(Sentry.replayIntegration());
};

export const setSentryUser = (user: AuthUser | null) => {
  if (user) {
    // Signed-in product monitoring runs regardless of the public-site banner
    enableSentryReplay();
    Sentry.setUser({
      username: user.username,
      id: user.username,
      userType: getUserType(user),
      cognitoId: user.userId,
    });
  } else {
    Sentry.setUser(null);
  }
};

type DebugInfo = {
  fn: string;
  variables?: Context | undefined;
};

// Errors currently can be of type Error | NativeModuleError | AppleError
type CaptureExceptionProp = DebugInfo & {
  error: unknown;
};

export const captureException = ({
  error,
  fn,
  variables,
}: CaptureExceptionProp) => {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(`error in fn: ${fn}`, error);
  }
  Sentry.captureException(error, {
    contexts: {
      debug: { fn, variables, clientTimestamp: dayjs().toISOString() },
    },
  });
};

type CaptureMessageParams = DebugInfo & {
  message: string;
};

export const captureMessage = ({
  message,
  fn,
  variables,
}: CaptureMessageParams) => {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(
      `Message: ${message}. Fn: ${fn} Variables: ${JSON.stringify(variables)}`,
    );
  }

  Sentry.captureMessage(message, {
    contexts: {
      debug: {
        fn,
        variables,
        clientTimestamp: dayjs().toISOString(),
      },
    },
  });
};
